// React
import { Fragment, useMemo, useState } from "react"

// Router
import { Routes } from "@/constants/routes"

// Queries
import {
	ProjectProjectObligationsStatusEnum,
	PublicProjectType,
	useProjectsStatsTableQuery,
} from "@/api/graphql"

// Translate
import { useTrans } from "@/i18n"

// Tables
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
	CellContext,
	createColumnHelper,
	SortingState,
} from "@/lib/table"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"

// UI
import { cn } from "@/lib/utils"
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { SearchInput } from "@/components/form-controls/Input"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"
import { Link } from "@/components/Anchor"
import { PageTemplate } from "@/layouts/PageTemplate"

// Constants
import {
	mapCoordinatesCenterOfNetherlands,
	ProjectCrowdfundingTypes,
} from "@/constants/constants"

/**
 * StatisticsProjects
 * @returns
 */
export function StatisticsProjects() {
	// State
	const [filter, setFilter] = useState<string | undefined>()
	const limit = 20
	const [currentPage, setCurrentPage] = useState<number>(0)
	const [sorting, setSorting] = useState<SortingState>([])

	// Translations
	const t = useTrans(["project", "common"])

	// Query
	const { data } = useProjectsStatsTableQuery({
		latitude: mapCoordinatesCenterOfNetherlands.lat,
		longitude: mapCoordinatesCenterOfNetherlands.lng,
		radius: "500",
		limit,
		offset: currentPage * limit,
		name__icontains: filter,
		type: ProjectCrowdfundingTypes,
	})

	// Memo
	const projects = useMemo(() => {
		return (
			(data?.public_projects?.results?.filter(
				Boolean,
			) as PublicProjectType[]) || []
		)
	}, [data?.public_projects?.results])

	// PaginatorInfo
	const totalProjectsCount = useMemo(
		() => data?.public_projects?.totalCount ?? 0,
		[data?.public_projects?.totalCount],
	)

	// Tables
	const columnHelper = createColumnHelper<PublicProjectType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor((data) => data, {
				id: "name",
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.projects_table.header.project",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<PublicProjectType, PublicProjectType>,
				) => (
					<TableDataCell>
						<Link
							className={({ isActive }) =>
								cn(
									isActive && "active-navigation-item",
									"block truncate text-sm hover:text-gray-500",
								)
							}
							href={Routes.StatisticsPlatformSingleProject.replace(
								":id",
								info.getValue().id,
							)}
						>
							{info.getValue().name}
						</Link>
					</TableDataCell>
				),
			}),
			columnHelper.accessor("type", {
				header: () => (
					<TableHeading
						fieldName="type"
						sort={sorting}
						setSort={setSorting}
						sortType="client"
					>
						{t(
							"payments:payments.overview.header.number_of_shares",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, string>) => (
					<TableDataCell className="font-medium">
						<div className="inline-block rounded-md bg-primary-500 px-4 py-1 font-bold text-black opacity-90 shadow-2xl md:ml-4 md:mt-2 lg:mt-0">
							{t(`common:common.project.type.${info.getValue()}`)}
						</div>
					</TableDataCell>
				),
			}),
			columnHelper.accessor("start", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:dashboard.stats.projects_table.header.start_date",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, string>) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor((data) => data?.internal_rate_of_return, {
				id: "internal_rate_of_return",
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:dashboard.stats.platform_stats.disclaimer.terms.current_expected_irr.text",
						)}
					>
						{t(
							"dashboard:dashboard.stats.projects_table.header.current_expected_irr",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, number>) => (
					<TableDataCell className="font-medium">
						{info.getValue()
							? `${info.getValue().toFixed(2)}%`
							: null}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("project_obligations_status", {
				header: () => (
					<TableHeading
						tooltip={t(
							"dashboard:dashboard.stats.platform_stats.disclaimer.terms.obligations.text",
						)}
					>
						{t("common:common.project.obligations_status")}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						PublicProjectType,
						ProjectProjectObligationsStatusEnum
					>,
				) => (
					<TableDataCell className="font-medium">
						{t(
							`common:common.project.obligations_status.${info.getValue()}`,
						)}
					</TableDataCell>
				),
			}),
		],
		[t],
	)

	// Table
	const table = useReactTable({
		columns,
		data: projects,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),

		enableHiding: true,
		state: {
			pagination: {
				pageIndex: 0,
				pageSize: limit,
			},
			sorting,
			columnVisibility: {
				// TODO: Re-enable these once calcs are fixed
				internal_rate_of_return: false,
				project_obligations_status: false,
			},
		},

		// Sorting
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		enableSorting: true,
	})

	// Data state
	return (
		<PageTemplate backHref={Routes.StatisticsPlatform}>
			<CardWrapper className="mb-4">
				<CardBody>
					<div className="md:flex">
						<Heading as="h5" className="mb-3 sm:truncate">
							{t(
								"dashboard:dashboard.stats.projects_table.title",
							)}
						</Heading>
						<div className="mb-4 ml-auto flex gap-4">
							<div className="sm:ml-auto">
								<SearchInput
									onChange={(event) => {
										setFilter(event.currentTarget.value)
									}}
									label={"Zoeken"}
									className="md:width-auto min-w-full"
								/>
							</div>
						</div>
					</div>

					<Table
						className="min-w-[64rem] lg:min-w-0"
						data-testid="tablebody"
					>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => {
								return (
									<TableRowCell key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<Fragment key={header.id}>
													{flexRender(
														header.column.columnDef
															.header,
														header.getContext(),
													)}
												</Fragment>
											)
										})}
									</TableRowCell>
								)
							})}
						</TableHead>
						<TableBody
							data-testid="tablebody-overview"
							data-pageindex={
								table.getState().pagination.pageIndex
							}
						>
							{table.getRowModel().rows.map((row) => {
								const isOdd = row.index % 2 === 0
								return (
									<Fragment key={row.id}>
										<TableRowCell isOdd={isOdd}>
											{row
												.getVisibleCells()
												.map((cell, index) => {
													return (
														<Fragment key={index}>
															{flexRender(
																cell.column
																	.columnDef
																	.cell,
																cell.getContext(),
															)}
														</Fragment>
													)
												})}
										</TableRowCell>
									</Fragment>
								)
							})}
							{/* Pads the last entries in the table so the table doesn't collapse in the UI */}
							{table.getRowModel().rows.length <
								table.getState().pagination.pageSize &&
							table.getState().pagination.pageIndex !== 0 ? (
								<>
									{Array(
										Math.max(
											table.getState().pagination
												.pageSize -
												table.getRowModel().rows.length,
											1,
										),
									)
										.fill(true)
										.map((_, index) => (
											<TableRowCell
												key={index}
												withHover={false}
												isOdd={index % 2 === 0}
											>
												<TableDataCell
													colSpan={columns.length}
												>
													<span className="dummy-text" />
												</TableDataCell>
											</TableRowCell>
										))}
								</>
							) : null}
						</TableBody>
					</Table>
					<CardBody>
						{totalProjectsCount !== 0 && (
							<PaginationAsButtons
								countPerPage={limit}
								totalCount={totalProjectsCount ?? 0}
								itemType={
									"common.pagination.item_types.project"
								}
								currentPage={currentPage + 1}
								currentItemsAmount={totalProjectsCount ?? 0}
								onNextPage={() =>
									setCurrentPage(
										(currentPage) => currentPage + 1,
									)
								}
								onPrevPage={() =>
									setCurrentPage((currentPage) =>
										Math.max(currentPage - 1, 0),
									)
								}
								analyticsId="investments"
							/>
						)}
					</CardBody>
				</CardBody>
			</CardWrapper>
		</PageTemplate>
	)
}
